import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/components/Dialog';
import { NavButton } from '@/shared/components/NavButton';
import { PillButton } from '@/shared/components/PillButton';
import { TextInput } from '@/shared/components/TextInput';
import { copyIcon } from '@/assets';
import { DialogHeading, PlayerInvitationSection, CountdownSection } from './DialogShared';
import { getGameTypeButtonConfig } from '@/shared/utils/gameTypeHelpers';
import { createTextStroke } from '@/styles/textStroke';
import type { PlayerPresence, RoomGameType } from 'shared-types';

interface PrivateLobbyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  gameType: RoomGameType;
  onToggleGameType: () => void;
  onCreateRoom: () => void;
  lobbyCode: string | null;
  onCopyCode: () => void;
  isCodeCopied: boolean;
  players?: PlayerPresence[];
  isLoading?: boolean;
  countdown?: number | null;
  isStarting?: boolean;
  isCreator?: boolean;
  onStartNow?: () => void;
}

export function PrivateLobbyDialog({
  isOpen,
  onClose,
  gameType,
  onToggleGameType,
  onCreateRoom,
  lobbyCode,
  onCopyCode,
  isCodeCopied,
  players = [],
  isLoading = false,
  countdown = null,
  isStarting = false,
  isCreator = false,
  onStartNow = () => {
    // No-op default function
  }
}: PrivateLobbyDialogProps) {
  const { t } = useTranslation();

  // Get button configuration based on game type
  const { variant: buttonVariant, arrowDirection, textKey } = getGameTypeButtonConfig(gameType);
  const buttonText = t(textKey);

  // Room not yet created - show game type selection and create button
  const isRoomCreated = lobbyCode !== null;

  return (
    <Dialog isOpen={isOpen} onClose={onClose} variant="green" title={t('landing.playWithFriends')}>
      <div className="flex flex-col gap-6">
        {/* Section 1: Game Mode (toggleable before room creation) */}
        <div className="flex flex-col gap-3">
          <DialogHeading>{t('landing.gameMode')}</DialogHeading>
          <NavButton
            variant={buttonVariant}
            arrowDirection={arrowDirection}
            onClick={onToggleGameType}
            disabled={isLoading || isRoomCreated}
            className="w-full h-12"
          >
            {buttonText}
          </NavButton>
        </div>

        {/* Before room creation: Show create button */}
        {!isRoomCreated && !isLoading && (
          <PillButton
            variant="green"
            onClick={onCreateRoom}
            className="w-full h-12"
            icon={
              <span
                className="font-mind-boggle text-[#F2F2F2] leading-none text-lg whitespace-nowrap"
                style={{ fontWeight: 1000, ...createTextStroke('1.5px') }}
              >
                {t('landing.createRoom')}
              </span>
            }
          />
        )}

        {/* Loading state while creating room */}
        {isLoading && (
          <div className="flex gap-3 items-center">
            <TextInput
              variant="grey"
              value={t('common.loading')}
              readOnly
              className="flex-1 h-12"
            />
          </div>
        )}

        {/* After room creation: Show lobby code and players */}
        {isRoomCreated && (
          <>
            {/* Section 2: Lobby Code */}
            <div className="flex gap-3 items-center">
              <TextInput variant="grey" value={lobbyCode} readOnly className="flex-1 h-12" />
              <PillButton
                variant={isCodeCopied ? 'green' : 'gold'}
                onClick={onCopyCode}
                icon={<img src={copyIcon} alt="" className="w-6 h-6" />}
                className="w-12 h-12"
              />
            </div>

            {/* Section 3: Countdown or Player Invitations */}
            {isStarting && countdown !== null ? (
              <CountdownSection
                countdown={countdown}
                isCreator={isCreator}
                onStartNow={onStartNow}
              />
            ) : (
              <>
                <PlayerInvitationSection players={players} />
                {/* Show start button for creator when 2+ players and not yet starting */}
                {isCreator && players.length >= 2 && (
                  <NavButton variant="green" onClick={onStartNow} className="w-full h-12">
                    {t('landing.startGame')}
                  </NavButton>
                )}
              </>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
